import React from 'react';
import { useIntl } from 'react-intl';
import translations from './../../utils/translations';
import { useDataHelper } from './../../utils/dataHelper';

import { theme } from '../../theme';
import { styled } from '@material-ui/core/styles';

import { breakoutRoomInviteModel } from './../../entities/breakoutRoomInviteModel';
import { breakoutRoomModel } from './../../entities/breakoutRoomModel';

import Card from '@material-ui/core/Card';
import Grid from '@material-ui/core/Grid';
import Tooltip from '@material-ui/core/Tooltip';

import { SdmTypography, SdmTextButton, SdmSpacer } from '..';

const StyledCard = styled(Card)(({ xsdown }) => ({
  backgroundColor: theme.colors.primary.white,
  borderLeft: `${xsdown ? 5 : 10}px solid ${theme.colors.primary.red}`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: xsdown ? 10 : 20,
  marginBottom: 16,
  borderRadius: 0,
}));

const CustomCard = (
  { cardInformations, functions, isXsDown, isSmDown, ...rest },
  ref
) => {
  const { name, status, type } = cardInformations;
  const { onAccept, onDecline, onDetails, onEnter } = functions;
  // Hooks
  const intl = useIntl();
  const i18n = translations.personalArea(intl).cards;
  const {
    checkEnterButtonIsDisabled,
    getTooltipText,
    checkPassedDate,
    getFormattedDate,
  } = useDataHelper();

  const handleAccept = () => {
    onAccept && onAccept(cardInformations);
  };

  const handleDecline = () => {
    onDecline && onDecline(cardInformations);
  };

  const handleDetails = () => {
    onDetails && onDetails(cardInformations);
  };

  const handleEnter = () => {
    onEnter && onEnter(cardInformations);
  };

  const isChatType = () => {
    return type === breakoutRoomModel.enums.types.CHAT;
  };

  return (
    <StyledCard ref={ref} {...rest} xsdown={isXsDown ? 1 : 0} elevation={0}>
      <Grid container alignItems={'center'}>
        <Grid item xl={5} lg={3} md={3} sm={6} xs={12}>
          <Grid
            container
            alignItems={'flex-start'}
            direction={'column'}
            justify={isXsDown ? 'flex-start' : 'flex-start'}
          >
            <SdmTypography
              margin={isXsDown ? 0 : 1}
              style={{ marginBottom: 0, marginTop: 0 }}
              variant={isXsDown ? 'h3' : isSmDown ? 'h2' : 'h1'}
            >
              {name}
            </SdmTypography>
            <SdmTypography
              margin={isXsDown ? 0 : 1}
              style={{ marginBottom: 0, marginTop: 0 }}
              variant={isXsDown ? 'h5' : isSmDown ? 'h4' : 'h3'}
            >
              {i18n.types[type || breakoutRoomModel.enums.types.BREAKOUT]}
            </SdmTypography>
          </Grid>
        </Grid>
        {isXsDown && <SdmSpacer height={5} />}
        <Grid
          item
          xl={3}
          lg={4}
          md={3}
          sm={6}
          xs={12}
          style={{ marginBottom: isXsDown ? 8 : 0 }}
        >
          <Grid
            container
            alignItems={'center'}
            spacing={isXsDown ? 0 : 1}
            justify={'space-around'}
          >
            <Grid item sm={4} xs={12}>
              <SdmTypography
                variant={'text'}
                margin={0}
                textAlign={'left'}
                style={{
                  color: theme.colors.primary.grey,
                  marginBottom: isXsDown ? 10 : 0,
                }}
              >
                {getFormattedDate(cardInformations, isChatType())}
              </SdmTypography>
            </Grid>
            <Grid item sm={4} xs={12}>
              <SdmTypography
                variant={'text'}
                margin={0}
                textAlign={'left'}
                style={{
                  color: theme.colors.primary.grey,
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  marginBottom: isXsDown ? 10 : 0,
                }}
                onClick={handleDetails}
              >
                {i18n.details}
              </SdmTypography>
            </Grid>
            <Grid item sm={4} xs={12}>
              {status ? (
                <SdmTypography
                  variant={'text'}
                  margin={0}
                  style={{
                    color: theme.colors.primary.grey,
                    textTransform: 'uppercase',
                    marginBottom: isXsDown ? 10 : 0,
                  }}
                  textAlign={'left'}
                >
                  {status}
                </SdmTypography>
              ) : null}
            </Grid>
          </Grid>
        </Grid>
        {isXsDown && <SdmSpacer height={5} />}

        <Grid item xl={4} lg={5} md={6} sm={12} xs={12}>
          <Grid
            container
            justify={isSmDown ? 'space-around' : 'flex-end'}
            alignItems={'center'}
          >
            {!checkPassedDate(cardInformations?.end) ? (
              <>
                {status !== breakoutRoomInviteModel.enums.status.REJECTED ? (
                  <SdmTextButton
                    variant={'red'}
                    text={i18n.decline}
                    textVariant={'h4Regular'}
                    onClick={handleDecline}
                    style={{
                      minWidth: isXsDown ? 103 : 158,
                      height: isXsDown ? 46 : isSmDown ? 46 : 64,
                    }}
                  />
                ) : null}
                {status === breakoutRoomInviteModel.enums.status.PENDING ? (
                  <SdmTextButton
                    variant={'redFill'}
                    text={i18n.accept}
                    textVariant={'h4Regular'}
                    onClick={handleAccept}
                    style={{
                      minWidth: isXsDown ? 103 : 158,
                      height: isXsDown ? 46 : isSmDown ? 46 : 64,
                    }}
                  />
                ) : null}
                {status === breakoutRoomInviteModel.enums.status.ACCEPTED ? (
                  <Tooltip
                    title={getTooltipText(cardInformations, isChatType())}
                  >
                    <span>
                      <SdmTextButton
                        variant={'redFill'}
                        text={i18n.enter}
                        textVariant={'h4Regular'}
                        onClick={handleEnter}
                        disabled={checkEnterButtonIsDisabled(cardInformations)}
                        style={{
                          minWidth: isXsDown ? 103 : 158,
                          height: isXsDown ? 46 : isSmDown ? 46 : 64,
                        }}
                      />
                    </span>
                  </Tooltip>
                ) : null}
              </>
            ) : (
              <SdmTypography
                variant={'textBold'}
                margin={0}
                style={{
                  color: theme.colors.primary.red,
                  textTransform: 'uppercase',
                  marginBottom: isXsDown ? 10 : 0,
                }}
                textAlign={'center'}
              >
                {i18n.brOver}
              </SdmTypography>
            )}
          </Grid>
        </Grid>
      </Grid>
    </StyledCard>
  );
};

CustomCard.displayName = 'BiggerCardUser';

export const BiggerCardUser = React.forwardRef(CustomCard);
